import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createApiClient } from "../../Network/ApiClient";

const featuresApi = createApiClient("features");


const QA_NAV_PREFIX = "sidebar-navbar";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const qaId = (suffix: string | number) => `${QA_NAV_PREFIX}-${qaSafe(suffix)}`;

const qaAttrs = (suffix: string | number) => ({
  id: qaId(suffix),
  "data-testid": qaId(suffix),
  "data-qa": qaId(suffix),
});

// ─── Feature Key Map: route → feature key ─────────────────────────────────────
const featureKeyMap: Record<string, string> = {
  activityNotCovered:                "ACTIVITY_NOT_COVERED",
  activityAllowed:                   "ACTIVITY_ALLOWED",
  activityNotAllowed:                "ACTIVITY_NOT_ALLOWED",
  ActivityFrequency:                 "FREQUENCY_CONTROL",
  activityAllowedIcd:                "ACTIVITY_ALLOWED_ICD",
  activityNotAllowedICD:             "ACTIVITY_NOT_ICD",
  activityNotAllowedIncludeActivity: "ACTIVITY_INCLUDED_ACTIVITY",
  activitiesReqirePreApprovals:      "ACTIVITY_PRE_APPROVAL",
  iCDNotCovered:                     "ICD_NOT_COVERED",
  icdAllowed:                        "ICD_ALLOWED",
  icdNotAllowed:                     "ICD_NOT_ALLOWED",
  RelationAddAgeAndGender:           "RELATION_AGE_GENDER",
  PrincipalDiagnosed:                "PRINCIPAL_DIAGNOSED",
  benefit:                           "BENEFIT",
};

// ─── All Menu Items ───────────────────────────────────────────────────────────
const allMenuItems = [
  { to: "dashboard", icon: "bi bi-house-door-fill", label: "Dashboard" },
  {
    to: "userManagement",
    icon: "bi-people-fill",
    label: "User Management",
    roles: ["system_admin", "super_super_admin"],
  },
  {
    to: "featureControl",
    icon: "bi-toggles",
    label: "Feature Control",
    roles: ["system_admin"],
  },
  
  {
    to: "#submenu1",
    icon: "bi-file-medical",
    label: "ICD Configuration",
    subItems: [
      { to: "MedicalIcdGroups",        label: "Medical ICD Groups" },
      { to: "CustomIcdGroups",         label: "Custom ICD Groups" },
      { to: "RelationAddAgeAndGender", label: "ICD Age and Gender" },
      { to: "PrincipalDiagnosed",      label: "Not Allowed Principal Diagnosis" },
      { to: "iCDNotCovered",           label: "ICD Not Covered" },
      { to: "icdAllowed",              label: "ICD Must Come" },
      { to: "icdNotAllowed",           label: "ICD Not Allowed" },
    ],
  },
  {
    to: "#submenu2",
    icon: "bi-activity",
    label: "Activity Configuration",
    subItems: [
      { to: "ActivityMedicalGroup",              label: "Activity Medical Group" },
      { to: "ActivityCustomGroup",               label: "Activity Custom Group" },
      { to: "activityNotCovered",                label: "Activity Not Covered" },
      { to: "activityAllowed",                   label: "Activities Together" },
      { to: "activityNotAllowed",                label: "Activities Not Allowed Together" },
      { to: "ActivityFrequency",                 label: "Activity Frequency" },
      { to: "activityAllowedIcd",                label: " Activity Must Come ICDs" },
      { to: "activityNotAllowedICD",             label: "Activity Not Allowed with ICD" },
      { to: "activityBundle", label: "Activity Bundle" },
      { to: "activityAgeGender",                 label: "Activity Age and Gender" },
      { to: "activitiesReqirePreApprovals",      label: "Activity Pre-Approvals" },
    ],
  },
  {
    to: "#submenu4",
    icon: "bi-shield-check",
    label: "Benefit Configuration",
    subItems: [
      { to: "benefitGroup", label: "Benefit Group" },
      { to: "benefit",      label: "Benefit" },
    ],
  },
  {
    to: "#submenu5",
    icon: "bi-check-circle",
    label: "Validation Configuration",
    subItems: [
      { to: "claimValidation",     label: "Claim Validation" },
      { to: "bulkClaimValidation", label: "Bulk Claim Validation" },
      { to: "validationHistory",   label: "Validation History" },
    ],
  },
  {
    to: "#submenu6",
    icon: "bi-gear",
    label: "Configure",
    subItems: [
      { to: "provider",        label: "Provider" },
      { to: "NewNetwork",      label: "Network" },
      { to: "Policy",          label: "Policy" },
      { to: "PatientRelation", label: "Patient" },
      { to: "Insurance",       label: "Insurance Plan" },
      { to: "doctor",          label: "Doctor" },
      { to: "specialty",       label: "Specialty" },
      { to: "vip",             label: "VIP" },
    ],
  },
];

// ─── Types ────────────────────────────────────────────────────────────────────
interface SubItem {
  to: string;
  label: string;
  disabled?: boolean;
}

interface MenuItemsProps {
  to: string;
  icon: string;
  label: string;
  subItems?: SubItem[];
  openIndex: string | null;
  setOpenIndex: (index: string | null) => void;
  parentMenu: string;
  collapsed: boolean;
}

// ─── MenuItem Component ───────────────────────────────────────────────────────
const MenuItems = ({
  to,
  icon,
  label,
  subItems,
  openIndex,
  setOpenIndex,
  parentMenu,
  collapsed,
}: MenuItemsProps) => {
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleSubmenu = () => {
    if (collapsed) return;
    if (openIndex === to) {
      setOpenIndex(null);
      localStorage.removeItem("openMenu");
    } else {
      setOpenIndex(to);
      localStorage.setItem("openMenu", to);
    }
  };

  const isActive      = openIndex === to;
  const isCurrentPath = location.pathname.includes(to);

  // إذا ما في سب-أيتمز خالص (مش موجودة من الأصل) ارجع null
  // بس لو كلهم disabled ما نخبيهم، نخليهم يظهروا
  if (subItems && subItems.length === 0) return null;

  const itemQaBase =
    label === "Validation Configuration"
      ? qaId("menu-validation-configuration")
      : qaId(`menu-item-${to}`);

  return (
    <li
      id={itemQaBase}
      data-testid={itemQaBase}
      data-qa={itemQaBase}
      data-qa-label={label}
      data-qa-route={to}
      data-qa-active={isActive || isCurrentPath ? "true" : "false"}
      data-qa-collapsed={collapsed ? "true" : "false"}
      className="nav-item my-1"
      style={{ position: "relative", margin: "0.25rem 0" }}
    >
      <Link
        id={`${itemQaBase}-link`}
        data-testid={`${itemQaBase}-link`}
        data-qa={`${itemQaBase}-link`}
        data-qa-label={label}
        data-qa-route={to}
        to={subItems ? "#" : to}
        className={`nav-link d-flex align-items-center justify-content-${collapsed ? "center" : "start"} ${
          isActive ? "active-menu-item" : ""
        } ${isCurrentPath && !subItems ? "active-route" : ""}`}
        style={{
          color: isActive || isCurrentPath ? "#4a9eff" : "rgba(255, 255, 255, 0.8)",
          padding: collapsed ? "0.75rem" : "0.75rem 1rem",
          borderRadius: "8px",
          transition: "all 0.3s ease",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          whiteSpace: "nowrap",
          background: isActive
            ? "rgba(74, 158, 255, 0.2)"
            : isCurrentPath && !subItems
            ? "rgba(74, 158, 255, 0.15)"
            : "transparent",
          borderLeft: isCurrentPath && !subItems ? "3px solid #4a9eff" : "none",
        }}
        onClick={(e) => {
          if (subItems) {
            e.preventDefault();
            toggleSubmenu();
          }
        }}
        onMouseEnter={() => collapsed && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        title={collapsed ? label : ""}
      >
        <i
          id={`${itemQaBase}-icon`}
          data-testid={`${itemQaBase}-icon`}
          data-qa={`${itemQaBase}-icon`}
          className={`bi ${icon}`}
          style={{ fontSize: "20px", minWidth: "24px" }}
        ></i>
        {!collapsed && (
          <>
            <span
              id={`${itemQaBase}-label`}
              data-testid={`${itemQaBase}-label`}
              data-qa={`${itemQaBase}-label`}
              className="ms-2 flex-grow-1"
            >
              {label}
            </span>
            {subItems && (
              <i
                id={`${itemQaBase}-chevron`}
                data-testid={`${itemQaBase}-chevron`}
                data-qa={`${itemQaBase}-chevron`}
                data-qa-expanded={isActive ? "true" : "false"}
                className={`bi ${isActive ? "bi-chevron-up" : "bi-chevron-down"}`}
                style={{ fontSize: "12px" }}
              ></i>
            )}
          </>
        )}
      </Link>

      {/* Tooltip when collapsed */}
      {collapsed && showTooltip && (
        <div
          id={`${itemQaBase}-tooltip`}
          data-testid={`${itemQaBase}-tooltip`}
          data-qa={`${itemQaBase}-tooltip`}
          className="sidebar-tooltip"
        >
          {label}
        </div>
      )}

      {/* Sub-items */}
      {!collapsed && subItems && isActive && (
        <ul
          id={`${itemQaBase}-submenu`}
          data-testid={`${itemQaBase}-submenu`}
          data-qa={`${itemQaBase}-submenu`}
          className="submenu list-unstyled ps-4 mt-1"
        >
          {subItems.map((subItem) => {
            const isSubItemActive = location.pathname.includes(subItem.to);
            const subItemQaBase =
              subItem.to === "claimValidation"
                ? qaId("submenu-claim-validation")
                : qaId(`submenu-item-${subItem.to}`);

            // ── Disabled sub-item ──────────────────────────────────────────
            if (subItem.disabled) {
              return (
                <li
                  key={subItem.to}
                  id={subItemQaBase}
                  data-testid={subItemQaBase}
                  data-qa={subItemQaBase}
                  data-qa-label={subItem.label}
                  data-qa-route={subItem.to}
                  data-qa-disabled="true"
                  className="sub-menu-item my-1"
                >
                  <div
                    id={`${subItemQaBase}-disabled-link`}
                    data-testid={`${subItemQaBase}-disabled-link`}
                    data-qa={`${subItemQaBase}-disabled-link`}
                    className="nav-link py-2 d-flex align-items-center"
                    title="This feature is currently disabled. Enable it from Feature Control."
                    style={{
                      color: "rgba(255, 255, 255, 0.25)",
                      cursor: "not-allowed",
                      userSelect: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      id={`${subItemQaBase}-label`}
                      data-testid={`${subItemQaBase}-label`}
                      data-qa={`${subItemQaBase}-label`}
                      className="ms-2"
                    >
                      {subItem.label}
                    </span>
                    <span
                      id={`${subItemQaBase}-off-badge`}
                      data-testid={`${subItemQaBase}-off-badge`}
                      data-qa={`${subItemQaBase}-off-badge`}
                      style={{
                        fontSize: "9px",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        background: "rgba(239, 68, 68, 0.15)",
                        color: "rgba(239, 68, 68, 0.6)",
                        borderRadius: "4px",
                        padding: "1px 5px",
                        flexShrink: 0,
                      }}
                    >
                      OFF
                    </span>
                  </div>
                </li>
              );
            }

            // ── Normal sub-item ────────────────────────────────────────────
            return (
              <li
                key={subItem.to}
                id={subItemQaBase}
                data-testid={subItemQaBase}
                data-qa={subItemQaBase}
                data-qa-label={subItem.label}
                data-qa-route={subItem.to}
                data-qa-active={isSubItemActive ? "true" : "false"}
                data-qa-disabled="false"
                className="sub-menu-item my-1"
              >
                <Link
                  id={`${subItemQaBase}-link`}
                  data-testid={`${subItemQaBase}-link`}
                  data-qa={`${subItemQaBase}-link`}
                  data-qa-label={subItem.label}
                  data-qa-route={subItem.to}
                  to={subItem.to}
                  className={`nav-link py-2 ${isSubItemActive ? "active-sub-item" : ""}`}
                  onClick={() => {
                    localStorage.setItem("openMenu", parentMenu || to);
                  }}
                >
                  <span
                    id={`${subItemQaBase}-label`}
                    data-testid={`${subItemQaBase}-label`}
                    data-qa={`${subItemQaBase}-label`}
                    className="ms-2"
                  >
                    {subItem.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
interface NavbarProps {
  collapsed?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ collapsed = false }) => {
  const location = useLocation();
  const [openIndex, setOpenIndex]             = useState<string | null>(null);
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>([]);

  // ── Fetch enabled features ──────────────────────────────────────────────────
  useEffect(() => {
    featuresApi
      .get("")
      .then((res) => {
        const enabled: string[] = (res.data.features || [])
          .filter((f: any) => f.is_enabled)
          .map((f: any) => f.feature_key as string);
        setEnabledFeatures(enabled);
      })
      .catch(() => {
        // silently fail — show all items as enabled if fetch fails
      });
  }, []);

  // ── Filter helper: map → disabled flag instead of removing ─────────────────
  const filterSubItems = (
    subItems?: Array<{ to: string; label: string }>
  ): SubItem[] | undefined => {
    if (!subItems) return undefined;
    return subItems.map((sub) => {
      const featureKey = featureKeyMap[sub.to];
      // No feature key mapped → always enabled (e.g. Medical/Custom groups)
      if (!featureKey) return { ...sub, disabled: false };
      // Mark as disabled if feature is not enabled
      return {
        ...sub,
        disabled: !enabledFeatures.includes(featureKey),
      };
    });
  };

  // ── Role filtering ──────────────────────────────────────────────────────────
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  })();
  const currentRole = storedUser?.role ?? "super_admin";
  const menuItems = allMenuItems.filter(
    (item) => !item.roles || item.roles.includes(currentRole)
  );

  // ── Open menu persistence ───────────────────────────────────────────────────
  useEffect(() => {
    if (collapsed) {
      setOpenIndex(null);
    } else {
      const savedMenu = localStorage.getItem("openMenu");
      if (savedMenu) setOpenIndex(savedMenu);
    }
  }, [collapsed]);

  useEffect(() => {
    if (!collapsed) {
      const savedMenu = localStorage.getItem("openMenu");
      if (savedMenu) setOpenIndex(savedMenu);
    }
  }, [location.pathname, collapsed]);

  return (
    <div
      {...qaAttrs("container")}
      data-qa-collapsed={collapsed ? "true" : "false"}
      className="sidebar-container h-100"
      style={{ height: "100%" }}
    >
      <nav {...qaAttrs("nav")} className="sidebar-nav" style={{ padding: "0.5rem 0" }}>
        <ul
          {...qaAttrs("menu-list")}
          className="nav nav-pills flex-column p-2"
          style={{ listStyle: "none", margin: 0, padding: "0.5rem" }}
        >
          {menuItems.map((item) => {
            const filteredSubItems = filterSubItems(item.subItems);
            return (
              <MenuItems
                key={item.to}
                {...item}
                subItems={filteredSubItems}
                openIndex={openIndex}
                setOpenIndex={setOpenIndex}
                parentMenu={item.to}
                collapsed={collapsed}
              />
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Navbar;